import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Menu, LogOut, Shield, Settings } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useBudget } from '@/hooks/useBudget';
import { useNavigate } from 'react-router-dom';
import { lightImpact } from '@/utils/haptics';

const Header = () => {
  const { user, logout, isAdmin } = useAuth();
  const budget = useBudget();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const budgetColor = budget.status === 'danger' ? 'text-red-500' : budget.status === 'warning' ? 'text-amber-500' : 'text-emerald-500';
  const budgetBg = budget.status === 'danger' ? 'bg-red-500' : budget.status === 'warning' ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <header className="safe-top fixed top-0 left-0 right-0 z-40 glass border-b border-slate-200/50 dark:border-slate-800/50">
      <div className="flex items-center justify-between px-4 h-16 max-w-4xl mx-auto">
        {/* Left: Avatar + Name */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-full gradient-bg flex items-center justify-center flex-shrink-0 shadow-glow">
            <span className="text-sm font-bold text-white">
              {user?.name?.charAt(0).toUpperCase() || '?'}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
              Hi, {user?.name || 'Guest'}
            </p>
          </div>
        </div>

        {/* Center: Budget Pill */}
        <div className="flex-shrink-0 mx-2">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <span className="text-sm">💰</span>
            <div className="w-16 h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${budget.percentage}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className={`h-full rounded-full ${budgetBg}`}
              />
            </div>
            <span className={`text-xs font-bold ${budgetColor}`}>
              {budget.percentage}%
            </span>
          </div>
        </div>

        {/* Right: Menu */}
        <div className="relative flex-shrink-0">
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={async () => { await lightImpact(); setMenuOpen(!menuOpen); }}
            className="p-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors min-h-12 min-w-12 flex items-center justify-center"
            aria-label="Menu"
          >
            <Menu className="w-5 h-5 text-slate-600 dark:text-slate-300" />
          </motion.button>

          <AnimatePresence>
            {menuOpen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-40"
                  onClick={() => setMenuOpen(false)}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -8 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -8 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-slate-800 rounded-2xl shadow-elevated border border-slate-200 dark:border-slate-700 z-50 overflow-hidden"
                >
                  {isAdmin && (
                    <motion.button
                      whileTap={{ scale: 0.98 }}
                      onClick={async () => { await lightImpact(); setMenuOpen(false); navigate('/admin'); }}
                      className="w-full flex items-center gap-3 px-4 py-3.5 text-left text-sm font-medium text-slate-700 dark:text-slate-200
                                 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors min-h-12"
                    >
                      <Shield className="w-4 h-4 text-primary-600" />
                      Admin Panel
                    </motion.button>
                  )}
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    onClick={async () => { await lightImpact(); setMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-left text-sm font-medium text-slate-700 dark:text-slate-200
                               hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors min-h-12"
                  >
                    <Settings className="w-4 h-4 text-slate-500" />
                    Settings
                  </motion.button>
                  <div className="border-t border-slate-100 dark:border-slate-700" />
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    onClick={async () => { await lightImpact(); setMenuOpen(false); logout(); }}
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-left text-sm font-medium text-red-600 dark:text-red-400
                               hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors min-h-12"
                  >
                    <LogOut className="w-4 h-4" />
                    Log Out
                  </motion.button>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
};

export default Header;
